import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { type CommonState } from './reducers/common.js';
import { type DrawersState } from './reducers/drawers.js';
import { type LayoutState } from './reducers/layout.js';
import { type PendingActivityEventsState } from './reducers/pendingActivityEvents.js';
import { type RouterNetworkByPatientState } from './reducers/routerNetwork.js';
import { type RootState } from './store.js';

const selectCommon = (state: RootState) => state.common;
const selectDrawers = (state: RootState) => state.drawers;
const selectLayout = (state: RootState) => state.layout;
const selectPendingActivityEvents = (state: RootState) => state.pendingActivityEvents;
const selectRouterNetwork = (state: RootState) => state.routerNetwork;

export const getLayoutEditScope = createDraftSafeSelector(
  selectLayout,
  (state: LayoutState) => state.editScope,
);
export const isLayoutEditing = (scope: string) =>
  createDraftSafeSelector(selectLayout, (state: LayoutState) => state.editScope === scope);

// Commons
export const isVideoPlayingSelector = (patientId: string) =>
  createDraftSafeSelector([selectCommon], (state: CommonState) => {
    if (typeof state.patientsProfiles[patientId]?.isVideoPlaying !== 'undefined') {
      return state.patientsProfiles[patientId].isVideoPlaying;
    }
    return true;
  });
export const isEmergencyInProgressSelector = (patientId: string) =>
  createDraftSafeSelector([selectCommon], (state: CommonState) => {
    if (typeof state.patientsProfiles[patientId]?.emergencyInProgress !== 'undefined') {
      return state.patientsProfiles[patientId].emergencyInProgress;
    }
    return undefined;
  });
export const getColorMode = createDraftSafeSelector(selectCommon, (state: CommonState) => state.colorMode);

// Drawers
export const isFilterDrawerOpen = createDraftSafeSelector(
  selectDrawers,
  (state: DrawersState) => state.filterDrawerOpen,
);
export const isCareInformationDrawerOpen = createDraftSafeSelector(
  selectDrawers,
  (state: DrawersState) => state.careInformationDrawerOpen,
);
export const isActivityReviewDrawerOpen = createDraftSafeSelector(
  selectDrawers,
  (state: DrawersState) => state.activityReviewDrawerOpen,
);
export const getSnackPack = createDraftSafeSelector(selectCommon, (state: CommonState) => state.snackPack);

// Pending activity events — events broadcast from the worker that have not yet
// been reviewed. The toast/drawer apply a 30-min live window on top.
const THIRTY_MIN_MS = 30 * 60 * 1000;

export const getPendingActivityEvents = createDraftSafeSelector(
  selectPendingActivityEvents,
  (state: PendingActivityEventsState) => {
    const cutoff = Date.now() - THIRTY_MIN_MS;
    return state.items.filter((e) => Date.parse(e.capturedAt) >= cutoff && !e.reviewStatus);
  },
);

export const getPendingActivityEventsForPatient = (patientId: string) =>
  createDraftSafeSelector(getPendingActivityEvents, (items) => items.filter((e) => e.patientId === patientId));

export const getRouterNetworkForPatient = (patientId: string) =>
  createDraftSafeSelector(
    selectRouterNetwork,
    (state: RouterNetworkByPatientState) => state.byPatientId[patientId],
  );
